import { SupportedLanguage } from '../i18n/translations';

export type ServiceSlug =
  | 'customs-clearance'
  | 'documents'
  | 'shipment-tracking'
  | 'hs-classification';

interface ServiceCopy {
  name: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  paragraphs: string[];
  benefitsTitle: string;
  benefits: string[];
  cta: string;
  quoteCta: string;
}

export const serviceSlugs: ServiceSlug[] = [
  'customs-clearance',
  'documents',
  'shipment-tracking',
  'hs-classification',
];

export const servicePages: Record<ServiceSlug, Record<SupportedLanguage, ServiceCopy>> = {
  'customs-clearance': {
    ar: {
      name: 'التخليص الجمركي',
      title: 'التخليص الجمركي في اللاذقية وسوريا | الشراع',
      description: 'تخليص جمركي للبضائع التجارية في اللاذقية عبر الموانئ والمعابر السورية. تقدير رسوم، بيان جمركي، ومتابعة الإفراج مع الشراع.',
      h1: 'التخليص الجمركي للبضائع التجارية في سوريا',
      intro: 'ننهي إجراءات التخليص الجمركي بدقة وسرعة للشحنات التجارية عبر البر والبحر والجو، ومقرنا في اللاذقية.',
      paragraphs: [
        'خدمة التخليص الجمركي تشمل إعداد البيان، متابعة الكشف، والتنسيق مع الجهات المختصة حتى الإفراج عن البضاعة.',
        'نخدم المستوردين والمصدّرين في سوريا وعملاء الدول العربية عبر مرفأ اللاذقية والمعابر البرية والمطارات.',
        'فريقنا يتابع كل مرحلة ويطلعكم على حالة الشحنة لتقليل التأخير والمفاجآت وأرضيات التخزين.',
        'اطلبوا تقديراً أولياً للرسوم وأتعاب التخليص قبل الشحن عبر واتساب أو نموذج عرض السعر على الموقع.',
      ],
      benefitsTitle: 'ماذا تشمل الخدمة؟',
      benefits: ['بيان جمركي كامل', 'متابعة الكشف والإفراج', 'تنسيق مع المعابر والموانئ', 'تقدير رسوم ودعم حتى التسليم'],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Customs Clearance',
      title: 'Commercial Customs Clearance | Al Shiraa',
      description: 'Customs clearance in Latakia across Syrian borders and ports for import and export.',
      h1: 'Commercial Customs Clearance',
      intro: 'We complete customs procedures with precision and speed for commercial cargo by land, sea and air.',
      paragraphs: [
        'The service covers the customs declaration, inspection follow-up, and coordination until release.',
        'We serve importers and exporters in Syria and the Arab region via Latakia port, land borders and airports.',
        'Our team updates you at every stage to reduce delays and surprises.',
      ],
      benefitsTitle: 'What is included?',
      benefits: ['Full customs declaration', 'Inspection and release follow-up', 'Border and port coordination', 'Support until delivery'],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Zollabfertigung',
      title: 'Zollabfertigung für Handelswaren | Al Shiraa',
      description: 'Zollabfertigung in Latakia über syrische Grenzen und Häfen für Import und Export.',
      h1: 'Zollabfertigung für Handelswaren',
      intro: 'Wir erledigen Zollformalitäten präzise und schnell für Handelswaren zu Land, zu Wasser und in der Luft.',
      paragraphs: [
        'Die Leistung umfasst Zollanmeldung, Beschau und Koordination bis zur Freigabe.',
        'Wir betreuen Importeure und Exporteure in Syrien und der arabischen Region.',
        'Unser Team hält Sie in jeder Phase auf dem Laufenden.',
      ],
      benefitsTitle: 'Was ist enthalten?',
      benefits: ['Vollständige Zollanmeldung', 'Begleitung von Beschau und Freigabe', 'Koordination an Grenzen und Häfen', 'Support bis zur Zustellung'],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },
  documents: {
    ar: {
      name: 'تجهيز الوثائق',
      title: 'تجهيز الوثائق والشهادات الجمركية | الشراع',
      description: 'تحضير الفاتورة وشهادة المنشأ وقائمة التعبئة وكافة الوثائق المطلوبة للاستيراد والتصدير في سوريا.',
      h1: 'تجهيز الوثائق والشهادات',
      intro: 'نحضر الوثائق الرسمية المطلوبة للتخليص الجمركي لتفادي احتجاز الشحنة بسبب نقص مستند.',
      paragraphs: [
        'نراجع الملف قبل وصول البضاعة ونتأكد من اكتمال الفاتورة التجارية، شهادة المنشأ، قائمة التعبئة، ووثيقة الشحن.',
        'حسب نوع البضاعة قد نطلب شهادات إضافية (صحية، مواصفات، سلامة) ونرشدكم إلى المطلوب مسبقاً.',
        'الهدف: وصول الشحنة جاهزة للإفراج دون تأخير إداري.',
      ],
      benefitsTitle: 'الوثائق التي نساعدكم فيها',
      benefits: ['فاتورة تجارية', 'شهادة المنشأ', 'قائمة التعبئة', 'وثائق إضافية حسب نوع البضاعة'],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Documents',
      title: 'Customs Documents & Certificates | Al Shiraa',
      description: 'Preparation of invoices, certificates of origin, packing lists and required import/export papers in Syria.',
      h1: 'Document & Certificate Handling',
      intro: 'We prepare the official papers needed for clearance so cargo is not held for missing documents.',
      paragraphs: [
        'We review the file before arrival and confirm the commercial invoice, certificate of origin, packing list and transport document.',
        'Depending on cargo type we may request extra certificates and guide you early.',
        'Goal: arrive ready for release without administrative delay.',
      ],
      benefitsTitle: 'Documents we help with',
      benefits: ['Commercial invoice', 'Certificate of origin', 'Packing list', 'Extra certificates by cargo type'],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Dokumente',
      title: 'Zolldokumente und Zertifikate | Al Shiraa',
      description: 'Vorbereitung von Rechnung, Ursprungszeugnis, Packliste und erforderlichen Im-/Exportpapieren in Syrien.',
      h1: 'Dokumenten- und Zertifikatsservice',
      intro: 'Wir bereiten die nötigen Unterlagen vor, damit die Ware nicht wegen fehlender Papiere gehalten wird.',
      paragraphs: [
        'Wir prüfen die Akte vor Ankunft und bestätigen Rechnung, Ursprungszeugnis, Packliste und Frachtbrief.',
        'Je nach Ware können zusätzliche Zertifikate nötig sein – wir weisen früh darauf hin.',
        'Ziel: Freigabe ohne administrative Verzögerung.',
      ],
      benefitsTitle: 'Unterlagen, bei denen wir helfen',
      benefits: ['Handelsrechnung', 'Ursprungszeugnis', 'Packliste', 'Zusatzzertifikate je nach Ware'],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },
  'shipment-tracking': {
    ar: {
      name: 'متابعة الشحنات',
      title: 'متابعة الشحنات حتى التسليم | الشراع',
      description: 'إشراف ومتابعة شحناتكم من الوصول إلى المعبر أو الميناء حتى التسليم النهائي في سوريا.',
      h1: 'متابعة الشحنات حتى التسليم',
      intro: 'نوفر إشرافاً عملياً على الشحنة في كل مرحلة: وصول، كشف، إفراج، وتحميل أو تسليم.',
      paragraphs: [
        'تبقى على اطلاع بحالة الملف دون الحاجة للتواجد شخصياً في المعبر أو الميناء.',
        'ننسّق بين النقل البري أو البحري أو الجوي وإجراءات التخليص لتقليل زمن الانتظار.',
        'مناسب للشركات خارج سوريا التي تحتاج شريكاً محلياً موثوقاً للمتابعة اليومية.',
      ],
      benefitsTitle: 'ما الذي تتابعونه معنا؟',
      benefits: ['تحديثات مرحلية', 'تنسيق الكشف والتحميل', 'ربط التخليص بالنقل', 'نقطة اتصال واحدة'],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'Shipment Tracking',
      title: 'Shipment Supervision Until Delivery | Al Shiraa',
      description: 'Hands-on follow-up of your cargo from border or port arrival until final delivery in Syria.',
      h1: 'Shipment Supervision Until Delivery',
      intro: 'We supervise each stage: arrival, inspection, release, loading or delivery.',
      paragraphs: [
        'Stay informed without being physically present at the border or port.',
        'We coordinate land, sea or air transport with clearance to cut waiting time.',
        'Ideal for companies outside Syria that need a reliable local partner for daily follow-up.',
      ],
      benefitsTitle: 'What you get',
      benefits: ['Stage updates', 'Inspection and loading coordination', 'Clearance linked to transport', 'One contact point'],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'Sendungsverfolgung',
      title: 'Sendungsbetreuung bis zur Zustellung | Al Shiraa',
      description: 'Praktische Begleitung Ihrer Sendung von Ankunft an Grenze oder Hafen bis zur Endzustellung in Syrien.',
      h1: 'Sendungsverfolgung bis zur Zustellung',
      intro: 'Wir betreuen jede Phase: Ankunft, Beschau, Freigabe, Verladung oder Zustellung.',
      paragraphs: [
        'Sie bleiben informiert, ohne vor Ort an Grenze oder Hafen sein zu müssen.',
        'Wir verknüpfen Transport und Zollabfertigung, um Wartezeiten zu verkürzen.',
        'Ideal für Unternehmen außerhalb Syriens mit Bedarf an einem lokalen Partner.',
      ],
      benefitsTitle: 'Was Sie erhalten',
      benefits: ['Status-Updates', 'Koordination von Beschau und Verladung', 'Abfertigung mit Transport verknüpft', 'Eine Ansprechperson'],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },
  'hs-classification': {
    ar: {
      name: 'التصنيف الجمركي',
      title: 'استشارات التصنيف الجمركي HS Code | الشراع',
      description: 'مراجعة البنود الجمركية (HS Codes) لشحناتكم في سوريا لتفادي الأخطاء والغرامات والتأخير.',
      h1: 'استشارات التصنيف الجمركي (HS Code)',
      intro: 'نساعدكم على اختيار البند الجمركي الصحيح قبل أو أثناء التخليص لتجنب إعادة التصنيف والغرامات.',
      paragraphs: [
        'التصنيف الخاطئ يؤخر الإفراج ويرفع التكاليف. نراجع وصف البضاعة والمستندات ونقترح البند الأنسب.',
        'يمكن الجمع بين الاستشارة وخدمة التخليص الكاملة لنفس الشحنة.',
        'مفيد للمستوردين الجدد وللشحنات متعددة الأصناف.',
      ],
      benefitsTitle: 'فوائد التصنيف الصحيح',
      benefits: ['تقليل مخاطر الغرامات', 'تسريع الإفراج', 'رسوم أوضح مسبقاً', 'ملف جاهز للبيان الجمركي'],
      cta: 'تواصل عبر واتساب',
      quoteCta: 'اطلب عرض سعر',
    },
    en: {
      name: 'HS Classification',
      title: 'HS Code Classification Consulting | Al Shiraa',
      description: 'Review of HS codes for your shipments in Syria to avoid errors, penalties and delays.',
      h1: 'HS Code Classification Consulting',
      intro: 'We help you pick the right tariff code before or during clearance to avoid reclassification and fines.',
      paragraphs: [
        'Wrong classification delays release and raises cost. We review the goods description and documents and suggest the best code.',
        'Consulting can be combined with full clearance for the same shipment.',
        'Useful for new importers and multi-item cargo.',
      ],
      benefitsTitle: 'Benefits of correct classification',
      benefits: ['Lower penalty risk', 'Faster release', 'Clearer fees upfront', 'File ready for the declaration'],
      cta: 'Chat on WhatsApp',
      quoteCta: 'Request a quote',
    },
    de: {
      name: 'HS-Einstufung',
      title: 'HS-Code-Beratung | Al Shiraa',
      description: 'Prüfung der HS-Codes Ihrer Sendungen in Syrien zur Vermeidung von Fehlern, Strafen und Verzögerungen.',
      h1: 'HS-Code-Beratung',
      intro: 'Wir helfen bei der richtigen Tarifierung vor oder während der Abfertigung.',
      paragraphs: [
        'Falsche Einstufung verzögert die Freigabe und erhöht Kosten. Wir prüfen Warenbeschreibung und Unterlagen.',
        'Die Beratung kann mit voller Abfertigung kombiniert werden.',
        'Nützlich für neue Importeure und Sendungen mit mehreren Positionen.',
      ],
      benefitsTitle: 'Vorteile korrekter Einstufung',
      benefits: ['Weniger Strafrisiko', 'Schnellere Freigabe', 'Klarere Gebühren im Voraus', 'Akte bereit für die Anmeldung'],
      cta: 'Per WhatsApp schreiben',
      quoteCta: 'Angebot anfordern',
    },
  },
};
